<template>
<div
  class='slide slide--video slide-video'
  :class="'slide-video--' + provider"
>
  <div
    class='slide-video_bg'
    :style='videoOverlayStyle'
  />

  <div
    class='slide-video_container'
    :class="containerClasses"
    @click='setPlayingStatus'
  >
    <!-- Show headline on top of the embed video if slide has resources -->
    <template v-if='has_resources && (provider === "embed" || provider === "youtube")'>
      <h2
        v-if='activity.headline'
        class='slide-video_headline h-three-light'
      >
        {{ activity.headline }}
      </h2>
    </template>

    <!-- Embed Container -->
    <div class='slide-video_embed'>
      <!-- TBK Interactive Video -->
      <InteractiveVideo
         v-if='provider === "tbk"'
        :isPlaying='isPlaying'
        :code='activity.code'
        :headline='activity.headline'
      />
    </div>

    <!-- Headline for Wistia video -->
    <template v-if='provider === "wistia"'>
      <h2
        v-if='activity.headline && !isPlaying'
        class='slide-video_headline h-three-light'
        @click='play'
      >
        {{ activity.headline }}
        <i />
      </h2>
    </template>

    <template v-else-if='provider === "tbk"' />

    <!-- Headline for Embed videos -->
    <template v-if='!has_resources && (provider === "embed" || provider === "youtube")'>
      <h2
        v-if='activity.headline'
        class='slide-video_headline h-three-light'
      >
        {{ activity.headline }}
      </h2>
    </template>

    <template v-if='hasEnded && !has_end_cta'>
      <div class='slide-video_headline slide-video_headline--ended'>
        <button
          v-if='typeof on_last_slide !== "undefined" && !on_last_slide'
          class='button-one-light'
          @click.prevent='goToNextSlide'
        >
          {{ $t('keep_learning') }}
        </button>

        <button
          class='button-two-light'
          @click.prevent='replay'
        >
          <ReplayIcon />
          {{ $t('replay') }}
        </button>
      </div>
    </template>
  </div>

  <!-- Resource Carousel -->
  <div
    v-if='has_resources'
    class='slide-video_resources'
    :class="{'slide-video_resources--activity' : has_activity_resources}"
  >
    <ResourcesCarousel
      :resources='resources'
      :show_headline='show_resources_headline'
      :pauseMedia='becameHidden'
      context='video'
      :toggleActivityOverlay='toggleResourceOverlay'
    />
  </div>
</div>
</template>

<script>
import CommonMixin from './common-mixin'

import TbkVideo from 'tbk-utils/video/tbk-video'
import InteractiveVideo from '../../components/videos_internal/InteractiveVideo'
import ResourcesCarousel from '../course/resources-carousel'

import ReplayIcon from '../icons/replay.svg'

export default {
  mixins: [CommonMixin],

  components: {
    InteractiveVideo,
    ResourcesCarousel,
    ReplayIcon
  },

  data() {
    return {
      vClient: null,
      isPlaying: false,
      hasEnded: false,

      video_plugins: null,
      video_width: null,
      video_height: null,
      aspect_ratio: null
    }
  },

  mounted() {
    if (this.provider === 'embed' && this.activity.code.match(/wistiaPlaylist/g)) {
      // Load Wistia API script
      if (document.querySelector('#wistia_api_script') === null) {
        const wistia_api_script = document.createElement('script')
        wistia_api_script.setAttribute('src', '//fast.wistia.com/assets/external/E-v1.js')
        wistia_api_script.async = true
        wistia_api_script.setAttribute('id', 'wistia_api_script')
        document.head.appendChild(wistia_api_script)
      }

      // Load Wistia Playlist API script
      if (document.querySelector('#wistia_playlist_api_script') === null) {
        const wistia_playlist_api_script = document.createElement('script')
        wistia_playlist_api_script.setAttribute('src', '//fast.wistia.com/assets/external/playlist-v1.js')
        wistia_playlist_api_script.async = true
        wistia_playlist_api_script.setAttribute('id', 'wistia_playlist_api_script')
        document.head.appendChild(wistia_playlist_api_script)
      }

      setTimeout(() => {
        this.initVideo()
      }, 2000)
    } else {
      this.initVideo()
    }

    window.addEventListener('orientationchange', () => {
      this._resizeIframeIfNeeded()
      this._resizeVideoToAspectRatio()
    })

    window.addEventListener('resize', () => {
      this._resizeIframeIfNeeded()
      this._resizeVideoToAspectRatio()
    })
  },

  watch: {
    video_height: function(val) {
      this._resizeVideoToAspectRatio()
    }
  },

  computed: {
    provider() {
      return this.activity.provider
    },

    videoOverlayStyle() {
      if (this.activity.background_image) {
        return `background-image: url(${this.activity.background_image})`
      }

      // Gradient Background
      if (!this.activity.background_image && this.activity.gradient_start) {
        const start = this.activity.gradient_start
        const end = this.activity.gradient_end || this.activity.gradient_start
        const gradient = `linear-gradient(137deg, ${start} 0%, ${end} 92%)`
        return {
          backgroundImage: gradient
        }
      }
      return null
    },

    containerClasses() {
      const classes = []

      if (this.activity.headline) classes.push('has-headline')
      if (this.has_resources) classes.push('has-resources')

      return classes
    },

    has_slide_resources() {
      return this.slide && this.slide.resources && this.slide.resources.length
    },

    has_activity_resources() {
      return this.activity && this.activity.resources && this.activity.resources.length
    },

    has_end_cta() {
      let result = false
      if (this.video_plugins) {
        this.video_plugins.forEach((plugin) => {
          if (plugin.options.ctaType &&       // Wistia video contains built in CTA
            plugin.options.on &&            // and it is turned on
            plugin.options.time &&          // and the time of CTA is set
            plugin.options.time === 'end' // and the time is at the end of video
          ) {
            result = true
          }
        })
      }
      return result
    },

    has_resources() {
      return this.has_slide_resources || this.has_activity_resources
    },

    show_resources_headline() {
      return this.slide && this.slide.resource_carousel_headline_visible
    },

    resources() {
      if (this.has_slide_resources) return this.slide.resources

      if (this.has_activity_resources) {
        return this.activity.resources.map((r) => r.activity)
      }
      return null
    }
  },

  methods: {
    setPlayingStatus(e) {
      if (this.isPlaying || e.target.className.match(/video-overlay/g)) return false
      this.isPlaying = true
    },

    initVideo() {
      this.getEmbedCode()
    },

    becameVisible() {
      this.markSlideAsViewed()
      this.vClient && this.vClient.readyPlay($(this.$el).find('.slide-video_embed')[0])
    },

    becameHidden() {
      this.vClient && this.vClient.pause($(this.$el).find('.slide-video_embed')[0])
    },

    play() {
      this.vClient && this.vClient.play($(this.$el).find('.slide-video_embed')[0])
    },

    replay() {
      this.hasEnded = false
      this.play()
    },

    getEmbedCode() {
      this.vClient = TbkVideo.new(this.activity.provider, this.activity.code)

      const that = this
      this.vClient.getEmbedCode(function(code, video) {
        if (this.activity.provider === 'wistia') {
          this.loadWistiaPlugins(video, this.activity.complete_threshold)

          // Hack to support vertical videos
          _wq.push({
            id: that.activity.code,
            options: {
              videoFoam: true,
              playButton: false
            },
            onReady: function(video) {
              that.video_width = video.data.media.assets[0].width
              that.video_height = video.data.media.assets[0].height
              that.video_plugins = video.hasPlugins() ? video._pluginScripts : null

              // make video container visible after video was resized
              video.bind('heightchange', () => {
                $(that.$el).find('.slide-video_embed').css({ opacity: '1' })
              })

              // track video end event
              video.bind('end', () => {
                that.onWistiaEnd()
              })
            }
          })

          this._resizeIframeIfNeeded()
        }

        if (this.activity.provider !== 'tbk') {
          $(this.$el).find('.slide-video_embed').html(code)
        }
      }.bind(this))
    },

    // This function sets fixed width and height for iframe for iOS devices
    // There's iOS bug when it crops iframe content after device rotate
    _resizeIframeIfNeeded() {
      const iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)
      if (window.innerWidth <= 1024 && iOS) {
        const container = $(this.$el).find('.slide-video_embed')

        setTimeout(() => {
          const width  = container.innerWidth()
          const height = container.innerHeight()
          const iframe = $(this.$el).find('iframe')

          iframe.css({ width, height })
        }, 600) // 600ms is animation time when outline menu hides/opens
      }
    },

    _resizeVideoToAspectRatio() {
      this.aspect_ratio = this.video_height / this.video_width
      let containerHeight = null
      let containerWidth = null

      if (this.aspect_ratio > 0.5625) {
        if ($(window).outerWidth() < 480) {
          // mobile
          containerWidth = $(window).outerWidth() * 0.6 // this number is magic (60% of screen window)
          containerHeight = containerWidth * this.aspect_ratio
        } else {
          // desktop
          containerHeight = $(window).outerHeight() * 0.7 // this number is magic (70% of screen height)
          containerWidth = containerHeight / this.aspect_ratio
        }
      }

      if (containerWidth && containerHeight) {
        $(this.$el).find('.slide-video_embed').css({
          'padding-bottom': '0',
          height: containerHeight,
          width: containerWidth
        })
        $(this.$el).find('.slide-video_headline').css({
          'max-width': containerWidth
        })
      } else {
        // make wistia video container visible after video was loaded
        $(this.$el).find('.slide-video_embed').css({ opacity: '1' })
      }
    },

    loadWistiaPlugins(video, percent_complete) {
      this.initWistiaCaptions()
      this.vClient.loadCrossTime(percent_complete, this.onComplete)
    },

    onComplete(video) {
      this.updateResult('watched')
    },

    onWistiaEnd() {
      this.hasEnded = true
      this.vClient.cancelFullscreen()
      if (!this.has_end_cta) { // if there is no CTA at the end of the video
        this.initVideo() // reinit video to make thumbnail visible
      }
    },

    initWistiaCaptions() {
      if (this.locale !== 'en') {
        this.vClient.loadCaptions(this.locale)
      }
    }

  }
}
</script>

<style lang='sass' scoped>
@import './video-v1'
</style>
